import React, { createContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../config/axios.js';
import { clearCompanyLogoCache } from '../hooks/useCompanyLogo.js';
import { clearDashboardCache } from '../utils/dashboardCache.js';

export const AuthContext = createContext();

const mergeSessionUser = (storedUser, serverData) => ({
  ...storedUser,
  ...serverData,
  token: storedUser.token,
  token_expiry: storedUser.token_expiry,
  company_features: serverData.company_features ?? storedUser.company_features,
  network_mode: storedUser.network_mode ?? serverData.network_mode,
  is_cargo_shipment: storedUser.is_cargo_shipment ?? serverData.is_cargo_shipment,
  // Prefer live server flags so Company Settings toggles apply after session verify
  is_crossing_enabled: serverData.is_crossing_enabled ?? storedUser.is_crossing_enabled,
  is_auto_ml_enabled: serverData.is_auto_ml_enabled ?? storedUser.is_auto_ml_enabled,
  require_paid_settlement_before_ml:
    serverData.require_paid_settlement_before_ml ?? storedUser.require_paid_settlement_before_ml,
  lock_ml_shipment_details_until_remit:
    serverData.lock_ml_shipment_details_until_remit ?? storedUser.lock_ml_shipment_details_until_remit,
  shipment_receipt_size: serverData.shipment_receipt_size ?? storedUser.shipment_receipt_size,
  booking_receipt_size: serverData.booking_receipt_size ?? storedUser.booking_receipt_size,
  delivery_receipt_size: serverData.delivery_receipt_size ?? storedUser.delivery_receipt_size,
  print_qr_with_booking_receipt:
    serverData.print_qr_with_booking_receipt ?? storedUser.print_qr_with_booking_receipt,
  qr_print_template: serverData.qr_print_template ?? storedUser.qr_print_template,
  location_hub_dispatch_types:
    serverData.location_hub_dispatch_types ?? storedUser.location_hub_dispatch_types,
  location_hub_destination_type:
    serverData.location_hub_destination_type ?? storedUser.location_hub_destination_type,
  can_create_ogcl_transfer:
    serverData.can_create_ogcl_transfer ?? storedUser.can_create_ogcl_transfer,
  location_hub_allow_delivery:
    serverData.location_hub_allow_delivery ?? storedUser.location_hub_allow_delivery,
  location_hub_allow_booking:
    serverData.location_hub_allow_booking ?? storedUser.location_hub_allow_booking,
  location_hub_allow_paid:
    serverData.location_hub_allow_paid ?? storedUser.location_hub_allow_paid,
  location_hub_allow_to_pay:
    serverData.location_hub_allow_to_pay ?? storedUser.location_hub_allow_to_pay,
  location_hub_allow_on_account:
    serverData.location_hub_allow_on_account ?? storedUser.location_hub_allow_on_account,
  location_hub_ownership_type:
    serverData.location_hub_ownership_type ?? storedUser.location_hub_ownership_type,
  can_submit_company_settlement:
    serverData.can_submit_company_settlement ?? storedUser.can_submit_company_settlement,
  own_hub_settle_collected_only:
    serverData.own_hub_settle_collected_only ?? storedUser.own_hub_settle_collected_only,
  labour_reserve_enabled:
    serverData.labour_reserve_enabled ?? storedUser.labour_reserve_enabled,
  is_pickup_man: serverData.is_pickup_man ?? storedUser.is_pickup_man,
  commission_enabled: serverData.commission_enabled ?? storedUser.commission_enabled,
  allowed_report_types:
    serverData.allowed_report_types ?? storedUser.allowed_report_types,
  role_module_access:
    serverData.role_module_access ?? storedUser.role_module_access,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const syncSessionFromServer = useCallback(async (storedUser) => {
    try {
      const response = await axiosInstance.get('/auth/verify-session');
      if (response.data?.authenticated && response.data.data) {
        const synced = mergeSessionUser(storedUser, response.data.data);
        sessionStorage.setItem('user', JSON.stringify(synced));
        setUser(synced);
        return synced;
      }
    } catch (err) {
      console.error('Session verification failed:', err);
      sessionStorage.removeItem('user');
      setUser(null);
      clearDashboardCache();
    }
    return null;
  }, []);

  // Check if user is already logged in on mount
  useEffect(() => {
    const verifySession = async () => {
      try {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);

          // Clear stale data if it still contains old username-based structure
          if (parsed?.username !== undefined) {
            sessionStorage.removeItem('user');
              setUser(null);
              clearDashboardCache();
              return;
            }

          // Check token expiry before trusting sessionStorage
          if (parsed?.token_expiry) {
            const expiry = new Date(parsed.token_expiry);
            if (expiry <= new Date()) {
              // Token expired — clear stale data, don't set user
              sessionStorage.removeItem('user');
              setUser(null);
              clearDashboardCache();
              return;
            }
          }

          if (parsed?.token) {
            await syncSessionFromServer(parsed);
          } else {
            setUser(parsed);
          }
        }
      } catch (err) {
        console.error('Error verifying session:', err);
        sessionStorage.removeItem('user');
        clearDashboardCache();
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, [syncSessionFromServer]);

  const login = useCallback(async (email, password, companyId = null) => {
    try {
      setError(null);
      const payload = { email, password };
      if (companyId) {
        payload.company_id = companyId;
      }
      const response = await axiosInstance.post('/auth/login', payload);
      if (response.data?.otp_required) {
        return response.data;
      }
      const userData = response.data.data;
      clearDashboardCache();
      setUser(userData);
      sessionStorage.setItem('user', JSON.stringify(userData));
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Login failed';
      setError(errorMsg);
      throw err;
    }
  }, []);

  const verifyLoginOtp = useCallback(async (challengeId, otp) => {
    try {
      setError(null);
      const response = await axiosInstance.post('/auth/login-otp/verify', {
        challenge_id: challengeId,
        otp,
      });
      const userData = response.data.data;
      clearDashboardCache();
      setUser(userData);
      sessionStorage.setItem('user', JSON.stringify(userData));
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Invalid OTP';
      setError(errorMsg);
      throw err;
    }
  }, []);

  const resendLoginOtp = useCallback(async (challengeId) => {
    try {
      setError(null);
      const response = await axiosInstance.post('/auth/login-otp/resend', {
        challenge_id: challengeId,
      });
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to resend OTP';
      setError(errorMsg);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axiosInstance.post('/auth/logout');
      setUser(null);
      sessionStorage.removeItem('user');
      clearCompanyLogoCache();
      clearDashboardCache();
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
      sessionStorage.removeItem('user');
      clearCompanyLogoCache();
      clearDashboardCache();
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      setError(null);
      const response = await axiosInstance.post('/auth/forgot-password', { email });
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to send reset link';
      setError(errorMsg);
      throw err;
    }
  }, []);

  const resetPassword = useCallback(async (email, otp, newPassword, confirmPassword) => {
    try {
      setError(null);
      const response = await axiosInstance.post('/auth/reset-password', {
        email,
        otp,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to reset password';
      setError(errorMsg);
      throw err;
    }
  }, []);

  const patchUser = useCallback((partial) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      sessionStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    verifyLoginOtp,
    resendLoginOtp,
    logout,
    forgotPassword,
    resetPassword,
    syncSessionFromServer,
    patchUser,
    isAuthenticated: !!user,
  };

  // Don't render children until auth state is resolved — prevents flash redirects
  if (loading) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
